import { formatInTimeZone } from 'date-fns-tz';
import type { IntlFormatters } from 'react-intl';

import { DateSalesWithTz } from '@eventbrite/event-listing-service-client';
import { getFormattedDateTime } from '@eventbrite/legacy-infra.datetime-fns';

import { BasicInfo } from '@/src/listings/types';

export const DEFAULT_TZ = 'UTC';
export const FULL_DATE_TIME_FORMAT = "yyyy-MM-dd'T'HH:mm:ssXXX";
export const DATE_ONLY_FORMAT = 'yyyy-MM-dd';
const DEFAULT_LOCALE = 'en-US';

const TWELVE_HOUR_LOCALES = ['en', 'en-US', 'en-GB', 'en-IE', 'en-AU', 'en-NZ'];

export const shouldShow12hCycle = (locale: string): boolean => {
  return TWELVE_HOUR_LOCALES.includes(locale);
};

type FormatMessage = IntlFormatters['formatMessage'];

export type DateObject = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  dayPeriod: string;
  timeZoneName: string;
};

export enum MonthFormat {
  LONG = 'long',
  SHORT = 'short',
}

export enum WeekdayFormat {
  LONG = 'long',
  SHORT = 'short',
}

export interface DateFormatOptions {
  locale: string;
  timezone: string;
  monthFormat?: MonthFormat | null;
  showYear?: boolean;
  weekday?: WeekdayFormat | null;
}

export const getAttendeeTimezone = (): string | undefined => {
  try {
    return new Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e: unknown) {
    console.error('Error getting attendee timezone from browser', e);
    return undefined;
  }
};

export const getDisplayTimezone = ({
  isOnlineEvent,
  eventTimezone,
  attendeeTimezone,
}: {
  isOnlineEvent: boolean;
  eventTimezone?: string | null;
  attendeeTimezone?: string;
}): string => {
  const normalizedEventTimezone = eventTimezone?.trim() ? eventTimezone : undefined;
  const normalizedAttendeeTimezone = attendeeTimezone?.trim() ? attendeeTimezone : undefined;

  if (isOnlineEvent) {
    return normalizedAttendeeTimezone ?? normalizedEventTimezone ?? DEFAULT_TZ;
  }

  return normalizedEventTimezone ?? DEFAULT_TZ;
};

/**
 * Event dates are stored as a naive local wall-clock (`local`) plus a timezone
 * name, with no UTC instant of their own. `local` is authoritative for the
 * event timezone, computed server-side. When we display an event in its own
 * timezone we must render `local` as a floating wall clock instead of
 * re-deriving it from `utc`, because the front-end's own tzdata (ICU, tzdata
 * package) can lag IANA and resolve recent zone-rule changes with a stale
 * offset — shifting the time (and sometimes the calendar day) by an hour.
 */
const shouldRenderEventLocal = (date: DateSalesWithTz, timezone: string): boolean => {
  if (!date.local) {
    return false;
  }

  const eventTimezone = date.timezone?.trim() || DEFAULT_TZ;
  return eventTimezone === timezone;
};

const toFloatingDate = (local: string): Date | null => {
  const normalized = /(?:Z|[+-]\d{2}:?\d{2})$/.test(local) ? local : `${local}Z`;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

type ResolvedDisplayDate = {
  date: Date;
  timeZone: string;
  // Floating dates are naive wall clocks anchored to UTC purely so Intl can
  // print them; they carry no absolute instant and must never be converted.
  floating: boolean;
};

const resolveDisplayDate = (date: DateSalesWithTz, timezone: string): ResolvedDisplayDate | null => {
  if (shouldRenderEventLocal(date, timezone)) {
    const floatingDate = toFloatingDate(date.local as string);
    if (floatingDate) {
      return { date: floatingDate, timeZone: DEFAULT_TZ, floating: true };
    }
  }

  if (date.utc) {
    const utcDate = new Date(date.utc);
    if (!Number.isNaN(utcDate.getTime())) {
      return { date: utcDate, timeZone: timezone, floating: false };
    }
  }

  return null;
};

type ResolvedDisplayPair = {
  start: ResolvedDisplayDate;
  end: ResolvedDisplayDate;
};

const resolveDisplayPair = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  timezone: string,
): ResolvedDisplayPair | null => {
  const start = resolveDisplayDate(startDate, timezone);
  const end = resolveDisplayDate(endDate, timezone);

  if (!start || !end) {
    return null;
  }

  // If only one side resolved floating they would be compared in different
  // frames; fall back to UTC instants so both share the display timezone.
  if (start.floating !== end.floating) {
    if (!startDate.utc || !endDate.utc) {
      return null;
    }
    return {
      start: { date: new Date(startDate.utc), timeZone: timezone, floating: false },
      end: { date: new Date(endDate.utc), timeZone: timezone, floating: false },
    };
  }

  return { start, end };
};

const getResolvedDatePart = (resolved: ResolvedDisplayDate, options: Intl.DateTimeFormatOptions): string =>
  new Intl.DateTimeFormat(DEFAULT_LOCALE, { timeZone: resolved.timeZone, ...options }).format(resolved.date);

const isSameDayResolved = (start: ResolvedDisplayDate, end: ResolvedDisplayDate): boolean =>
  getResolvedDatePart(start, { year: 'numeric', month: 'numeric', day: 'numeric' }) ===
  getResolvedDatePart(end, { year: 'numeric', month: 'numeric', day: 'numeric' });

const isSameMonthResolved = (start: ResolvedDisplayDate, end: ResolvedDisplayDate): boolean =>
  getResolvedDatePart(start, { month: 'numeric' }) === getResolvedDatePart(end, { month: 'numeric' });

const isSameYearResolved = (start: ResolvedDisplayDate, end: ResolvedDisplayDate): boolean =>
  getResolvedDatePart(start, { year: 'numeric' }) === getResolvedDatePart(end, { year: 'numeric' });

/**
 * Missing date data is a programming error in the comparison helpers: a silent
 * `false` would route an absent end date into the multi-day/range path. Throw
 * loudly instead; callers that can legitimately lack data (see
 * `GeneralEventPanelInfo`) wrap the call in a try/catch to fall back.
 *
 * A date is considered present when it carries either `utc` or the floating
 * `local`; an empty value counts as missing.
 */
const requireDateRange = (
  fnName: string,
  startDate?: DateSalesWithTz | null,
  endDate?: DateSalesWithTz | null,
): [DateSalesWithTz, DateSalesWithTz] => {
  if (!startDate || !endDate) {
    throw new Error(`${fnName}: start and end dates are required`);
  }
  if ((!startDate.utc && !startDate.local) || (!endDate.utc && !endDate.local)) {
    throw new Error(`${fnName}: start and end dates must have a utc or local value`);
  }
  return [startDate, endDate];
};

/**
 * Day/month/year comparisons in the event's timezone. When the target timezone
 * is the event's own, these consume the server-provided `local` wall clock
 * (floating) rather than re-deriving it from `utc`, so stale client tzdata
 * cannot shift the calendar day.
 *
 * These throw when a required date is missing or carries neither `utc` nor
 * `local` (see `requireDateRange`); they do not silently return `false`.
 */
export const isSameDayInTimezone = (
  startDate?: DateSalesWithTz | null,
  endDate?: DateSalesWithTz | null,
  timezone: string = DEFAULT_TZ,
): boolean => {
  const [start, end] = requireDateRange('isSameDayInTimezone', startDate, endDate);
  const pair = resolveDisplayPair(start, end, timezone);
  return pair ? isSameDayResolved(pair.start, pair.end) : false;
};

export const isSameMonthInTimezone = (
  startDate?: DateSalesWithTz | null,
  endDate?: DateSalesWithTz | null,
  timezone: string = DEFAULT_TZ,
): boolean => {
  const [start, end] = requireDateRange('isSameMonthInTimezone', startDate, endDate);
  const pair = resolveDisplayPair(start, end, timezone);
  return pair ? isSameMonthResolved(pair.start, pair.end) : false;
};

export const isSameYearInTimezone = (
  startDate?: DateSalesWithTz | null,
  endDate?: DateSalesWithTz | null,
  timezone: string = DEFAULT_TZ,
): boolean => {
  const [start, end] = requireDateRange('isSameYearInTimezone', startDate, endDate);
  const pair = resolveDisplayPair(start, end, timezone);
  return pair ? isSameYearResolved(pair.start, pair.end) : false;
};

type LocalDateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const getLocalDateTimeParts = (resolved: ResolvedDisplayDate): LocalDateTimeParts | null => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: resolved.timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    }).formatToParts(resolved.date);

    const getNumber = (type: Intl.DateTimeFormatPartTypes): number =>
      Number(parts.find((part) => part.type === type)?.value);

    const localParts = {
      year: getNumber('year'),
      month: getNumber('month'),
      day: getNumber('day'),
      hour: getNumber('hour'),
      minute: getNumber('minute'),
      second: getNumber('second'),
    };

    return Object.values(localParts).every(Number.isFinite) ? localParts : null;
  } catch {
    return null;
  }
};

export const isPastMidnightSingleNightEvent = (
  startDate?: DateSalesWithTz | null,
  endDate?: DateSalesWithTz | null,
): boolean => {
  // Unlike the same-day/month/year helpers, this is a best-effort classifier
  // that callers invoke unconditionally (e.g. `GeneralEventPanelInfo` calls it
  // outside its try/catch, as does `getEventDateTimeDisplayString` before the
  // end-date guards). Missing data here means "not a qualifying past-midnight
  // event", so it stays non-throwing and returns `false`.
  if (!startDate || !endDate) {
    return false;
  }

  const timezone = startDate.timezone?.trim() || endDate.timezone?.trim() || DEFAULT_TZ;
  const pair = resolveDisplayPair(startDate, endDate, timezone);

  if (!pair) {
    return false;
  }

  const start = getLocalDateTimeParts(pair.start);
  const end = getLocalDateTimeParts(pair.end);

  if (!start || !end) {
    return false;
  }

  const startDay = Date.UTC(start.year, start.month - 1, start.day);
  const endDay = Date.UTC(end.year, end.month - 1, end.day);
  const calendarDayDifference = (endDay - startDay) / (24 * 60 * 60 * 1000);
  const startSeconds = start.hour * 60 * 60 + start.minute * 60 + start.second;
  const endSeconds = end.hour * 60 * 60 + end.minute * 60 + end.second;

  return calendarDayDifference === 1 && startSeconds > 12 * 60 * 60 && endSeconds < 12 * 60 * 60;
};

export const bulletSeparator = '\u00A0•\u00A0';

const isCurrentYear = (date: DateSalesWithTz, locale: string, timezone: string): boolean => {
  const resolved = resolveDisplayDate(date, timezone);
  if (!resolved) {
    return false;
  }

  const getYear = (value: Date, timeZone: string) =>
    parseInt(new Intl.DateTimeFormat('en', { year: 'numeric', timeZone }).format(value), 10);

  const dateYear = getYear(resolved.date, resolved.timeZone);
  const currentYear = getYear(new Date(), timezone);

  return dateYear === currentYear;
};

const extractTimezoneFromFormatted = (formatted: string): string | null => {
  const match = formatted.match(/\s([A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5})$/);
  return match?.[1] || null;
};

const extractTimezoneFromIntl = (utcString: string, locale: string, timezone: string): string => {
  const timeZonePart = new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
    timeZone: timezone,
  })
    .formatToParts(new Date(utcString))
    .find((part) => part.type === 'timeZoneName');

  if (!timeZonePart?.value) {
    return '';
  }

  const abbrevMatch = timeZonePart.value.match(/[A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5}/);
  return abbrevMatch ? abbrevMatch[0] : timeZonePart.value.toUpperCase().slice(0, 5);
};

// Timezone abbreviations still derive from `utc` + a client-side zone lookup.
// `local` intentionally has no offset/suffix, so it cannot carry the
// abbreviation; this moves to a server-provided offset/abbreviation later.
const extractTimezoneAbbreviation = (utcString: string, locale: string, timezone: string): string => {
  const formatted = getFormattedDateTime(utcString, true, timezone, locale, 'p');
  const fromFormatted = extractTimezoneFromFormatted(formatted);

  if (fromFormatted) {
    return fromFormatted;
  }

  try {
    return extractTimezoneFromIntl(utcString, locale, timezone);
  } catch (error) {
    console.error('Error extracting timezone abbreviation', error);
    return '';
  }
};

const normalizeTimeFormat = (formatted: string): string =>
  formatted.replace(/:00\s*(AM|PM)/g, ' $1').replace(/(\d)(AM|PM)/g, '$1 $2');

const formatTimeFromParts = (hour: string, minute: string, dayPeriod: string): string => {
  // For 12-hour format (with AM/PM), omit :00 for whole hours
  // For 24-hour format (no AM/PM), always show minutes including :00
  const time = dayPeriod && minute === '00' ? hour : `${hour}:${minute}`;
  return dayPeriod ? `${time} ${dayPeriod.toUpperCase()}` : `${hour}:${minute}`;
};

const extractTimeFromFormatted = (formatted: string): string => {
  const timeMatch = formatted.match(/(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm))/i);
  return timeMatch
    ? normalizeTimeFormat(timeMatch[1])
    : normalizeTimeFormat(formatted.replace(/\s([A-Z]{2,5}(?:\+|-)\d{1,2}|[A-Z]{2,5})$/, ''));
};

const formatTimeOnly = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  const resolved = resolveDisplayDate(date, timezone);
  if (!resolved) {
    return '';
  }

  const hourCycle = shouldShow12hCycle(locale) ? 'h12' : 'h23';

  try {
    const parts = new Intl.DateTimeFormat(locale, {
      hour: 'numeric',
      minute: '2-digit',
      hourCycle: hourCycle,
      timeZone: resolved.timeZone,
    }).formatToParts(resolved.date);

    const getPart = (type: string) => parts.find((p) => p.type === type)?.value || '';
    const hour = getPart('hour');
    const minute = getPart('minute');
    const dayPeriod = getPart('dayPeriod');

    if (hour && minute) {
      return formatTimeFromParts(hour, minute, dayPeriod);
    }
  } catch (error) {
    console.error('Error formatting time', error);
  }

  if (resolved.floating || !date.utc) {
    return '';
  }

  const formatted = getFormattedDateTime(date.utc, true, timezone, locale, 'p');
  return extractTimeFromFormatted(formatted);
};

const createAtSeparator = (formatMessage: FormatMessage): string =>
  `'${formatMessage({ id: 'at', defaultMessage: 'at', description: 'Date joiner from day to hour' })}'`;

const getAtMessage = (formatMessage: FormatMessage): string =>
  formatMessage({ id: 'at', defaultMessage: 'at', description: 'Date joiner from day to hour' });

export const getFormattedDateForSingleDay = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  includeWeekday: boolean = true,
): string => {
  if (!date.utc && !date.local) {
    return '';
  }

  const addYear = !isCurrentYear(date, locale, timezone);
  const resolved = resolveDisplayDate(date, timezone);

  if (resolved?.floating) {
    const dateStr = getFormattedDateOnlyLocaleBased(resolved.date, {
      locale,
      monthFormat: MonthFormat.SHORT,
      timezone: resolved.timeZone,
      showYear: addYear,
      weekday: includeWeekday ? WeekdayFormat.LONG : null,
    });
    return normalizeTimeFormat(`${dateStr} ${getAtMessage(formatMessage)} ${formatTimeOnly(date, locale, timezone)}`);
  }

  const atSeparator = createAtSeparator(formatMessage);
  const datePart = includeWeekday ? `eeee, MMM d` : `MMM d`;
  const format = `${datePart}${addYear ? ', yyyy' : ''} ${atSeparator} p`;

  return normalizeTimeFormat(getFormattedDateTime(date.utc, true, timezone, locale, format));
};

export const getFormattedDateOnly = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  if (!date.utc && !date.local) {
    return '';
  }

  const addYear = !isCurrentYear(date, locale, timezone);
  const resolved = resolveDisplayDate(date, timezone);

  if (resolved?.floating) {
    return getFormattedDateOnlyLocaleBased(resolved.date, {
      locale,
      monthFormat: MonthFormat.SHORT,
      timezone: resolved.timeZone,
      showYear: addYear,
      weekday: WeekdayFormat.LONG,
    });
  }

  return getFormattedDateTime(date.utc, false, timezone, locale, `eeee, MMM d${addYear ? ', yyyy' : ''}`);
};

const getFormattedDateWithTimeIntl = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  dateTimeSeparator?: string,
  includeWeekday?: boolean,
  showTimezone: boolean = true,
): string => {
  if (!date.utc && !date.local) return '';
  const separator = dateTimeSeparator ?? bulletSeparator;
  const startsAt = formatMessage({
    id: 'Starts at',
    defaultMessage: 'Starts at',
    description: 'Label before start time when end time is hidden',
  });
  const dateFormatted = includeWeekday
    ? formatDateWithWeekday(date, locale, timezone, true)
    : formatDateWithoutWeekday(date, locale, timezone);
  const timeFormatted = formatTimeOnly(date, locale, timezone);
  const timezoneAbbrev =
    showTimezone && date.utc ? extractTimezoneAbbreviation(date.utc, locale, timezone) : '';

  return normalizeTimeFormat(
    `${dateFormatted} ${separator} ${startsAt} ${timeFormatted}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`,
  );
};

export const getSingleDateDisplayString = (
  date: DateSalesWithTz,
  showTime: boolean,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  includeWeekday: boolean = true,
  dateTimeSeparator?: string,
  useAbbreviatedWeekday?: boolean,
  showTimezone: boolean = true,
): string => {
  if (!date.utc && !date.local) {
    return '';
  }

  if (useAbbreviatedWeekday) {
    const datePart = formatDateWithAbbreviatedWeekday(date, locale, timezone);
    if (!showTime) return datePart;
    const separator = dateTimeSeparator ?? bulletSeparator;
    const timeFormatted = formatTimeOnly(date, locale, timezone);
    const timezoneAbbrev = date.utc ? extractTimezoneAbbreviation(date.utc, locale, timezone) : '';
    return normalizeTimeFormat(
      `${datePart}${separator}${timeFormatted}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`,
    );
  }
  if (showTime) {
    if (!includeWeekday) {
      return getFormattedDateWithTimeIntl(
        date,
        locale,
        timezone,
        formatMessage,
        dateTimeSeparator,
        undefined,
        showTimezone,
      );
    }
    return getFormattedDateForSingleDay(date, locale, timezone, formatMessage, includeWeekday);
  }
  return includeWeekday
    ? getFormattedDateOnly(date, locale, timezone)
    : formatDateWithoutWeekday(date, locale, timezone);
};

const formatTimezoneIfPresent = (timezone: string, show: boolean = true): string =>
  show && timezone ? ` ${timezone}` : '';

export const getFormattedDateForSameDayWithTimeRange = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  showTimezone: boolean = true,
): string => {
  if ((!startDate.utc && !startDate.local) || (!endDate.utc && !endDate.local)) {
    return '';
  }

  const dateFormatted = formatDateWithWeekday(startDate, locale, timezone, true);
  const timezoneAbbrev =
    showTimezone && startDate.utc ? extractTimezoneAbbreviation(startDate.utc, locale, timezone) : '';

  const startTime = formatTimeOnly(startDate, locale, timezone);
  const endTime = formatTimeOnly(endDate, locale, timezone);

  const result = `${dateFormatted} ${bulletSeparator} ${startTime} - ${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;

  return conditionallyRemoveTimezone(normalizeTimeFormat(result), showTimezone);
};

const conditionallyRemoveTimezone = (str: string, showTimezone: boolean): string => {
  if (showTimezone) return str;
  return str.replace(/\s+(GMT|UTC)[\+\-]\d{1,2}|\s+(?!AM\b|PM\b)[A-Z]{2,5}(?=\s|$)/g, '');
};

export const formatDateWithoutWeekday = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  const resolved = resolveDisplayDate(date, timezone);
  if (!resolved) return '';
  const showYear = !isCurrentYear(date, locale, timezone);
  return getFormattedDateOnlyLocaleBased(resolved.date, {
    locale,
    monthFormat: MonthFormat.SHORT,
    timezone: resolved.timeZone,
    showYear,
    weekday: null,
  });
};

const formatDateWithWeekday = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  longMonth: boolean = false,
  skipYear: boolean = false,
): string => {
  const resolved = resolveDisplayDate(date, timezone);
  if (!resolved) return '';

  const showYear = !skipYear && !isCurrentYear(date, locale, timezone);
  const monthFormat = longMonth ? MonthFormat.LONG : MonthFormat.SHORT;
  return getFormattedDateOnlyLocaleBased(resolved.date, {
    locale,
    monthFormat,
    timezone: resolved.timeZone,
    showYear,
    weekday: WeekdayFormat.LONG,
  });
};

export const formatDateWithAbbreviatedWeekday = (date: DateSalesWithTz, locale: string, timezone: string): string => {
  const resolved = resolveDisplayDate(date, timezone);
  if (!resolved) return '';

  const addYear = !isCurrentYear(date, locale, timezone);

  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...(addYear && { year: 'numeric' }),
    timeZone: resolved.timeZone,
  });

  return formatter.format(resolved.date);
};

const getFormattedDateRangeOnly = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
): string => {
  const startResolved = resolveDisplayDate(startDate, timezone);
  const endResolved = resolveDisplayDate(endDate, timezone);
  if (!startResolved || !endResolved) return '';

  // If same day, just return the start date
  if (isSameDayInTimezone(startDate, endDate, timezone)) {
    const addYear = !isCurrentYear(startDate, locale, timezone);
    if (startResolved.floating) {
      return getFormattedDateOnlyLocaleBased(startResolved.date, {
        locale,
        monthFormat: MonthFormat.SHORT,
        timezone: startResolved.timeZone,
        showYear: addYear,
        weekday: null,
      });
    }
    return getFormattedDateTime(startDate.utc, false, timezone, locale, `MMM d${addYear ? ', yyyy' : ''}`);
  }

  // For date ranges in the same year, only show year on the end date if it's not the current year
  const sameYear = isSameYearInTimezone(startDate, endDate, timezone);
  const showYearInStart = !sameYear && !isCurrentYear(startDate, locale, timezone);
  const showYearInEnd = !isCurrentYear(endDate, locale, timezone);

  const startFormatted = getFormattedDateOnlyLocaleBased(startResolved.date, {
    locale,
    monthFormat: MonthFormat.LONG,
    timezone: startResolved.timeZone,
    showYear: showYearInStart,
    weekday: WeekdayFormat.LONG,
  });
  const endFormatted = getFormattedDateOnlyLocaleBased(endResolved.date, {
    locale,
    monthFormat: MonthFormat.LONG,
    timezone: endResolved.timeZone,
    showYear: showYearInEnd,
    weekday: WeekdayFormat.LONG,
  });
  return `${startFormatted}-${endFormatted}`;
};

const getFormattedDateWithEndTime = (
  dateToDisplay: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if ((!dateToDisplay.utc && !dateToDisplay.local) || (!endDate.utc && !endDate.local)) return '';
  const endsAt = formatMessage({
    id: 'Ends at',
    defaultMessage: 'Ends at',
    description: 'Label before end time when start time is hidden',
  });
  const dateFormatted = formatDateWithWeekday(dateToDisplay, locale, timezone, true);
  const endTime = formatTimeOnly(endDate, locale, timezone);
  const timezoneAbbrev =
    showTimezone && endDate.utc ? extractTimezoneAbbreviation(endDate.utc, locale, timezone) : '';
  return `${dateFormatted} ${bulletSeparator} ${endsAt} ${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
};

export const getFormattedDateEndsAt = (
  date: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => getFormattedDateWithEndTime(date, date, locale, timezone, formatMessage, showTimezone);

const getFormattedDateRangeWithEndTimeOnly = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if ((!startDate.utc && !startDate.local) || (!endDate.utc && !endDate.local)) return '';
  const endsAt = formatMessage({
    id: 'Ends at',
    defaultMessage: 'Ends at',
    description: 'Label before end time when start time is hidden',
  });
  const skipStartYear = isSameYearInTimezone(startDate, endDate, timezone);
  const startFormatted = formatDateWithWeekday(startDate, locale, timezone, true, skipStartYear);
  const endFormatted = formatDateWithWeekday(endDate, locale, timezone, true);
  const endTime = formatTimeOnly(endDate, locale, timezone);
  const timezoneAbbrev = endDate.utc ? extractTimezoneAbbreviation(endDate.utc, locale, timezone) : '';
  return `${startFormatted}-${endFormatted} ${bulletSeparator} ${endsAt} ${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
};

const getFormattedDateRangeWithStartTimeOnly = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  formatMessage: FormatMessage,
  showTimezone: boolean = true,
): string => {
  if ((!startDate.utc && !startDate.local) || (!endDate.utc && !endDate.local)) return '';
  const startsAt = formatMessage({
    id: 'Starts at',
    defaultMessage: 'Starts at',
    description: 'Label before start time when end time is hidden',
  });
  const skipStartYear = isSameYearInTimezone(startDate, endDate, timezone);
  const startFormatted = formatDateWithWeekday(startDate, locale, timezone, true, skipStartYear);
  const endFormatted = formatDateWithWeekday(endDate, locale, timezone, true);
  const startTime = formatTimeOnly(startDate, locale, timezone);
  const timezoneAbbrev = startDate.utc ? extractTimezoneAbbreviation(startDate.utc, locale, timezone) : '';
  return `${startFormatted}-${endFormatted} ${bulletSeparator} ${startsAt} ${startTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
};

export const getFormattedDateForTwoDays = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
  showTimezone: boolean = true,
): string => {
  const startResolved = resolveDisplayDate(startDate, timezone);
  const endResolved = resolveDisplayDate(endDate, timezone);
  if (!startResolved || !endResolved) {
    return '';
  }

  const addYearToStart = !isSameYearInTimezone(startDate, endDate, timezone);
  const addYearToEnd = !isCurrentYear(endDate, locale, timezone);

  // Use locale-aware formatting instead of hardcoded English format
  const startDateFormatted = getFormattedDateOnlyLocaleBased(startResolved.date, {
    locale,
    monthFormat: MonthFormat.LONG,
    timezone: startResolved.timeZone,
    showYear: addYearToStart,
    weekday: WeekdayFormat.LONG,
  });

  const endDateFormatted = getFormattedDateOnlyLocaleBased(endResolved.date, {
    locale,
    monthFormat: MonthFormat.LONG,
    timezone: endResolved.timeZone,
    showYear: addYearToEnd,
    weekday: WeekdayFormat.LONG,
  });

  const timezoneAbbrev =
    showTimezone && startDate.utc ? extractTimezoneAbbreviation(startDate.utc, locale, timezone) : '';
  const startTime = formatTimeOnly(startDate, locale, timezone);
  const endTime = formatTimeOnly(endDate, locale, timezone);

  const result = `${startDateFormatted}-${endDateFormatted} ${bulletSeparator} ${startTime}-${endTime}${formatTimezoneIfPresent(timezoneAbbrev, showTimezone)}`;
  return conditionallyRemoveTimezone(normalizeTimeFormat(result), showTimezone);
};

const EMPTY_END_DATE: DateSalesWithTz = { utc: null, timezone: undefined } as unknown as DateSalesWithTz;

export type GetEventDateTimeDisplayStringParams = {
  startDate: DateSalesWithTz;
  endDate: DateSalesWithTz;
  hideStartDate: boolean;
  hideEndDate: boolean;
  locale: string;
  timezone: string;
  formatMessage: FormatMessage;
  isRecurringEvent: boolean;
  recurringEventLabel?: string;
  showTimezone?: boolean;
};

export const getEventDateTimeDisplayString = (params: GetEventDateTimeDisplayStringParams): string | null => {
  const {
    startDate,
    endDate: rawEndDate,
    hideStartDate,
    hideEndDate,
    locale,
    timezone,
    formatMessage,
    isRecurringEvent,
    recurringEventLabel,
    showTimezone = true,
  } = params;

  const endDate = rawEndDate ?? EMPTY_END_DATE;

  if (!startDate.utc && !startDate.local) {
    return null;
  }

  if (isRecurringEvent && recurringEventLabel) {
    return recurringEventLabel;
  }

  if (isPastMidnightSingleNightEvent(startDate, endDate)) {
    if (hideStartDate && hideEndDate) {
      return formatDateWithWeekday(startDate, locale, timezone, true);
    }

    if (!hideStartDate && hideEndDate) {
      return getFormattedDateWithTimeIntl(startDate, locale, timezone, formatMessage, undefined, true, showTimezone);
    }

    if (hideStartDate && !hideEndDate) {
      return getFormattedDateWithEndTime(startDate, endDate, locale, timezone, formatMessage, showTimezone);
    }

    return getFormattedDateForSameDayWithTimeRange(startDate, endDate, locale, timezone, showTimezone);
  }

  if (hideStartDate && hideEndDate) {
    if ((endDate.utc || endDate.local) && !isSameDayInTimezone(startDate, endDate, timezone)) {
      return getFormattedDateRangeOnly(startDate, endDate, locale, timezone);
    }
    return formatDateWithWeekday(startDate, locale, timezone, true);
  }

  if (!hideStartDate && hideEndDate) {
    if ((endDate.utc || endDate.local) && !isSameDayInTimezone(startDate, endDate, timezone)) {
      return getFormattedDateRangeWithStartTimeOnly(startDate, endDate, locale, timezone, formatMessage, showTimezone);
    }
    return getFormattedDateWithTimeIntl(startDate, locale, timezone, formatMessage, undefined, true, showTimezone);
  }

  if (hideStartDate && !hideEndDate) {
    if (!endDate.utc && !endDate.local) {
      return formatDateWithWeekday(startDate, locale, timezone, true);
    }
    if (isSameDayInTimezone(startDate, endDate, timezone)) {
      return getFormattedDateEndsAt(endDate, locale, timezone, formatMessage, showTimezone);
    }
    return getFormattedDateRangeWithEndTimeOnly(startDate, endDate, locale, timezone, formatMessage, showTimezone);
  }

  if (!endDate.utc && !endDate.local) {
    return getFormattedDateWithTimeIntl(startDate, locale, timezone, formatMessage, undefined, true, showTimezone);
  }

  if (isSameDayInTimezone(startDate, endDate, timezone)) {
    return getFormattedDateForSameDayWithTimeRange(startDate, endDate, locale, timezone, showTimezone);
  }

  return getFormattedDateForTwoDays(startDate, endDate, locale, timezone, showTimezone);
};

// `hideStartDate` / `hideEndDate` are display-only preferences that hide the
// time-of-day on the event page (see the "Display start/end time" toggles in the
// creation flow). They must NOT strip the date from machine-readable SEO metadata:
// schema.org/Event requires `startDate`, and dropping it disqualifies the event from
// Google's event rich results. When a date is hidden we still emit it, just without
// the time component (schema.org accepts a date-only value), which honors the
// organizer's intent to not advertise a specific time while keeping the event eligible.
//
// NOTE: These emit a machine-readable ABSOLUTE instant (raw `utc`), so they must
// keep using `utc` + timezone conversion rather than the floating `local`. Moving
// to a server-provided offset is a follow-up.
export const getFormattedStartDate = (basicInfo: BasicInfo) => {
  if (!basicInfo.startDate.utc) {
    return null;
  }
  const format = basicInfo.hideStartDate ? DATE_ONLY_FORMAT : FULL_DATE_TIME_FORMAT;
  return formatInTimeZone(new Date(basicInfo.startDate.utc), basicInfo.startDate.timezone || DEFAULT_TZ, format);
};

export const getFormattedEndDate = (basicInfo: BasicInfo) => {
  if (!basicInfo.endDate?.utc) {
    return null;
  }
  const format = basicInfo.hideEndDate ? DATE_ONLY_FORMAT : FULL_DATE_TIME_FORMAT;
  return formatInTimeZone(new Date(basicInfo.endDate.utc), basicInfo.endDate.timezone || DEFAULT_TZ, format);
};

export const getFormattedDateOnlyLocaleBased = (dateObj: Date, options: DateFormatOptions): string => {
  const { locale, timezone, monthFormat = MonthFormat.LONG, showYear = false, weekday = null } = options;

  const formatOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    timeZone: timezone,
  };

  if (monthFormat) {
    formatOptions.month = monthFormat;
  }

  if (showYear) {
    formatOptions.year = 'numeric';
  }

  if (weekday) {
    formatOptions.weekday = weekday;
  }

  const formatter = new Intl.DateTimeFormat(locale, formatOptions);
  return formatter.format(dateObj);
};

export const getSimpleFormattedDateForTwoDays = (
  startDate: DateSalesWithTz,
  endDate: DateSalesWithTz,
  locale: string,
  timezone: string,
): string => {
  const startResolved = resolveDisplayDate(startDate, timezone);
  const endResolved = resolveDisplayDate(endDate, timezone);
  if (!startResolved || !endResolved) {
    return '';
  }

  const sameMonth = isSameMonthInTimezone(startDate, endDate, timezone);
  const sameYear = isSameYearInTimezone(startDate, endDate, timezone);

  if (sameMonth && sameYear) {
    if (isSameDayInTimezone(startDate, endDate, timezone)) {
      return formatDateWithoutWeekday(startDate, locale, timezone);
    }

    const baseFormatted = formatDateWithoutWeekday(startDate, locale, timezone);
    const endDay = getResolvedDatePart(endResolved, { day: 'numeric' });
    // Replace the day with range, keeping the rest of the native format
    const dayPattern = /\b\d+\b/;
    return baseFormatted.replace(dayPattern, (match) => `${match}-${endDay}`);
  } else {
    // Different months: use full separate format for each date
    const startFormatted = formatDateWithoutWeekday(startDate, locale, timezone);
    const endFormatted = formatDateWithoutWeekday(endDate, locale, timezone);
    return `${startFormatted}-${endFormatted}`;
  }
};
